/**
 * Program Card Component - Display program information in a card
 */

import React from 'react';
import { GraduationCap, MapPin, Clock, DollarSign, Briefcase, Globe } from 'lucide-react';
import type { Program } from '../types';

interface ProgramCardProps {
  program: Program;
  onClick?: () => void;
}

export const ProgramCard: React.FC<ProgramCardProps> = ({ program, onClick }) => {
  const formatSalary = (salary?: number) => {
    if (!salary) return null;
    return `$${(salary / 1000).toFixed(0)}k`;
  };

  return (
    <div
      onClick={onClick}
      className="bg-card rounded-2xl p-6 border border-border hover:shadow-lg transition-all cursor-pointer group"
    >
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {program.name}
          </h3>
          {program.degree_type && (
            <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full whitespace-nowrap">
              {program.degree_type}
            </span>
          )}
        </div>

        {/* University Info */}
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <GraduationCap className="h-4 w-4" />
          <span className="font-medium">{program.university.name}</span>
        </div>
      </div>

      {/* Location */}
      <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
        <span className="text-lg">{program.university.country.flag_emoji}</span>
        <MapPin className="h-4 w-4" />
        <span>
          {program.university.city && `${program.university.city}, `}
          {program.university.country.name}
        </span>
      </div>

      {/* Program Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {program.duration_years && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-info" />
            <span className="text-foreground font-medium">
              {program.duration_years} {program.duration_years === 1 ? 'year' : 'years'}
            </span>
          </div>
        )}

        {program.tuition_usd && (
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="h-4 w-4 text-success" />
            <span className="text-foreground font-medium">
              ${program.tuition_usd.toLocaleString()}/yr
            </span>
          </div>
        )}

        {program.avg_starting_salary_usd && (
          <div className="flex items-center gap-2 text-sm">
            <Briefcase className="h-4 w-4 text-accent" />
            <span className="text-foreground font-medium">
              {formatSalary(program.avg_starting_salary_usd)} avg
            </span>
          </div>
        )}

        {program.is_online_available && (
          <div className="flex items-center gap-2 text-sm">
            <Globe className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Online available</span>
          </div>
        )}
      </div>

      {/* Field Tag */}
      {program.field && (
        <div className="pt-4 border-t border-border">
          <span className="inline-flex items-center px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
            {program.field}
          </span>
        </div>
      )}

      {/* Hover Effect */}
      <div className="mt-4 text-primary text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
        View Details →
      </div>
    </div>
  );
};
